import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ScheduleIcon from '@mui/icons-material/Schedule';
import VideocamIcon from '@mui/icons-material/Videocam';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import SchoolIcon from '@mui/icons-material/School';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Footer from '../../components/Footer';
import { cohorts, formatDate } from '../../data/liveClasses';
import { API_URL } from '../../config/api';
import { normalizePhone, PHONE_ERROR, PHONE_ALLOWED } from '../../utils/phone';
import './index.css';

const steps = [
  { icon: HowToRegIcon, title: 'Request to join', text: 'Pick a cohort and send a request. It takes under a minute.' },
  { icon: SupportAgentIcon, title: 'Talk to a mentor', text: 'We call you to understand your goals and confirm the right batch.' },
  { icon: SchoolIcon, title: 'Start learning live', text: 'Join interactive classes, build projects and get your doubts solved in real time.' }
];

const experienceOptions = ['Student', 'Fresher (0–1 years)', 'Working professional (1–3 years)', 'Working professional (3+ years)'];

const emptyForm = { name: '', emailAddress: '', phoneNumber: '', course: '', batch: '', experience: '', message: '' };

const LiveClasses = () => {
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [serverError, setServerError] = useState('');
  const formRef = useRef(null);
  const location = useLocation();

  // Pre-select the course when arriving from a link such as /live-classes?course=Agentic%20AI
  useEffect(() => {
    const course = new URLSearchParams(location.search).get('course');
    if (course && cohorts.some((c) => c.course.title === course)) {
      setFormData((prev) => ({ ...prev, course }));
    }
  }, [location.search]);

  // Support deep links such as /live-classes#request-form
  useEffect(() => {
    if (location.hash === '#request-form') {
      const timer = setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      return () => clearTimeout(timer);
    }
  }, [location.hash]);

  const handleRequest = (cohort) => {
    setFormData((prev) => ({ ...prev, course: cohort.course.title, batch: cohort.batch }));
    setStatus('idle');
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.emailAddress.trim()) {
      newErrors.emailAddress = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailAddress)) {
      newErrors.emailAddress = 'Please enter a valid email address';
    }
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!normalizePhone(formData.phoneNumber)) {
      newErrors.phoneNumber = PHONE_ERROR;
    }
    if (!formData.course) newErrors.course = 'Please choose a course';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setStatus('submitting');
    setServerError('');
    try {
      const response = await fetch(`${API_URL}/class-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Something went wrong');
      setStatus('success');
      setFormData(emptyForm);
    } catch (err) {
      console.error('Error submitting class request:', err);
      setServerError(err.message === 'Failed to fetch' ? 'Could not reach the server. Please try again.' : err.message);
      setStatus('error');
    }
  };

  return (
    <div className="page live-page">
      <section className="page-hero">
        <div className="container">
          <span className="section-eyebrow">Live Classes</span>
          <h1 className="page-hero-title">Learn live with industry mentors</h1>
          <p className="page-hero-subtitle">
            Small cohorts, real-time doubt solving and hands-on projects. Request a seat in an upcoming batch and our team will get in touch.
          </p>
        </div>
      </section>

      <section className="container live-steps">
        {steps.map(({ icon: Icon, title, text }, i) => (
          <div className="live-step" key={title}>
            <div className="live-step-icon"><Icon /></div>
            <div>
              <span className="live-step-number">Step {i + 1}</span>
              <h3>{title}</h3>
              <p>{text}</p>
            </div>
          </div>
        ))}
      </section>

      <section className="container">
        <h2 className="page-section-title">Upcoming cohorts</h2>
        <div className="cohort-grid">
          {cohorts.map((cohort) => {
            const { course } = cohort;
            const Icon = course.icon;
            return (
              <article
                className="cohort-card"
                key={cohort.id}
                style={{ '--accent-1': course.accent[0], '--accent-2': course.accent[1] }}
              >
                <div className="cohort-card-top">
                  <div className="cohort-icon"><Icon /></div>
                  <span className="cohort-batch">{cohort.batch}</span>
                </div>
                <h3>{course.title}</h3>
                <ul className="cohort-meta">
                  <li><CalendarMonthIcon sx={{ fontSize: 18 }} /> Starts {formatDate(cohort.startDate)}</li>
                  <li><ScheduleIcon sx={{ fontSize: 18 }} /> {cohort.schedule}</li>
                  <li><VideocamIcon sx={{ fontSize: 18 }} /> Live online · {cohort.duration}</li>
                </ul>
                <button type="button" className="cohort-cta" onClick={() => handleRequest(cohort)}>
                  Request to join
                </button>
              </article>
            );
          })}
        </div>
      </section>

      <section className="container live-form-section" ref={formRef} id="request-form">
        <div className="live-form-card">
          <div className="live-form-intro">
            <h2>Request a seat</h2>
            <p>Tell us a little about yourself. A mentor will call you within 1–2 working days to confirm your batch.</p>
            <ul>
              <li><CheckCircleIcon sx={{ fontSize: 18 }} /> No payment needed to request</li>
              <li><CheckCircleIcon sx={{ fontSize: 18 }} /> Free counselling call</li>
              <li><CheckCircleIcon sx={{ fontSize: 18 }} /> Recordings for every class</li>
            </ul>
          </div>

          {status === 'success' ? (
            <div className="live-form-success" role="status">
              <CheckCircleIcon sx={{ fontSize: 48 }} />
              <h3>Request received!</h3>
              <p>Thanks for your interest. Our team will contact you shortly.</p>
              <button type="button" className="btn-ghost" onClick={() => setStatus('idle')}>Send another request</button>
            </div>
          ) : (
            <form className="live-form" onSubmit={handleSubmit} noValidate>
              <div className="field">
                <label htmlFor="lc-name">Full name *</label>
                <input id="lc-name" name="name" value={formData.name} onChange={handleChange} placeholder="Your name" className={errors.name ? 'invalid' : ''} />
                {errors.name && <span className="field-error">{errors.name}</span>}
              </div>
              <div className="field-row">
                <div className="field">
                  <label htmlFor="lc-email">Email *</label>
                  <input id="lc-email" type="email" name="emailAddress" value={formData.emailAddress} onChange={handleChange} placeholder="you@example.com" className={errors.emailAddress ? 'invalid' : ''} />
                  {errors.emailAddress && <span className="field-error">{errors.emailAddress}</span>}
                </div>
                <div className="field">
                  <label htmlFor="lc-phone">Phone *</label>
                  <input
                    id="lc-phone"
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={(e) => PHONE_ALLOWED.test(e.target.value) && handleChange(e)}
                    placeholder="+91 98765 43210"
                    inputMode="tel"
                    autoComplete="tel"
                    maxLength={20}
                    className={errors.phoneNumber ? 'invalid' : ''}
                  />
                  {errors.phoneNumber && <span className="field-error">{errors.phoneNumber}</span>}
                </div>
              </div>
              <div className="field-row">
                <div className="field">
                  <label htmlFor="lc-course">Course *</label>
                  <select id="lc-course" name="course" value={formData.course} onChange={handleChange} className={errors.course ? 'invalid' : ''}>
                    <option value="">Select a course</option>
                    {cohorts.map((c) => <option key={c.id} value={c.course.title}>{c.course.title}</option>)}
                  </select>
                  {errors.course && <span className="field-error">{errors.course}</span>}
                </div>
                <div className="field">
                  <label htmlFor="lc-batch">Preferred batch</label>
                  <select id="lc-batch" name="batch" value={formData.batch} onChange={handleChange}>
                    <option value="">No preference</option>
                    <option value="Weekday">Weekday</option>
                    <option value="Weekend">Weekend</option>
                  </select>
                </div>
              </div>
              <div className="field">
                <label htmlFor="lc-experience">Current experience</label>
                <select id="lc-experience" name="experience" value={formData.experience} onChange={handleChange}>
                  <option value="">Select one</option>
                  {experienceOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div className="field">
                <label htmlFor="lc-message">Anything we should know?</label>
                <textarea id="lc-message" name="message" rows="3" value={formData.message} onChange={handleChange} placeholder="Your goals, questions or preferred timings" />
              </div>
              {status === 'error' && <div className="form-alert" role="alert">{serverError}</div>}
              <button type="submit" className="btn-gradient" disabled={status === 'submitting'}>
                {status === 'submitting' ? 'Sending…' : 'Send request'}
              </button>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LiveClasses;
